import { useId } from "react"
import { CaretDown, CircleNotch, MagnifyingGlass, X } from "@phosphor-icons/react"
import { Command as SearchPrimitive } from "cmdk"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"

export const SuggestionSearch = ({ className, ...props }: React.ComponentPropsWithRef<typeof SearchPrimitive>) => (
  <SearchPrimitive
    className={cn("bg-popover text-popover-foreground relative flex w-full flex-col rounded-md", className)}
    {...props}
  />
)

interface SearchInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  isLoading?: boolean
  value?: string
  error?: boolean
  dataTestId?: string
  onClear?: () => void
  ref?: React.Ref<HTMLInputElement>
  onClearButtonKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>
  wrapperClassName?: string
}

export const SearchInput = (props: SearchInputProps) => {
  const {
    value,
    className,
    isLoading = false,
    error,
    onClear,
    dataTestId,
    disabled,
    onKeyDown,
    onClearButtonKeyDown,
    wrapperClassName,
    ...rest
  } = props
  const id = useId()
  return (
    <div className={cn("relative", wrapperClassName)}>
      <label htmlFor={id} className="absolute top-1/2 right-1 -translate-y-1/2 p-2">
        {isLoading ? (
          <CircleNotch className="text-on-surface-subdued animate-spin" />
        ) : (
          <MagnifyingGlass className="text-on-surface-subdued" />
        )}
      </label>
      <input
        id={id}
        value={value}
        data-testid={`${dataTestId}-search-input`}
        className={cn(
          "peer border-border text-on-surface placeholder:text-on-surface-subdued hover:bg-surface-hovered focus:outline-primary! active:bg-surface-pressed h-12 w-full rounded-md border pt-0 pr-23.75 pl-4 text-sm outline-offset-0 transition-all focus:outline-2 disabled:cursor-not-allowed",
          error && "outline-critical! outline-2",
          className,
        )}
        autoFocus
        autoComplete="off"
        aria-autocomplete="none"
        disabled={disabled}
        onKeyDown={(event) => {
          const target = event.currentTarget
          if (event.key === "Home") {
            event.preventDefault()
            target.setSelectionRange(0, event.shiftKey ? target.selectionStart : 0)
          } else if (event.key === "End") {
            event.preventDefault()
            const range = target.value.length
            target.setSelectionRange(event.shiftKey ? target.selectionStart : range, range)
          }
          onKeyDown?.(event)
        }}
        {...rest}
      />
      {value && (
        <SearchClearButton
          onClick={onClear}
          data-testid="clear-search-button"
          disabled={disabled}
          onKeyDown={onClearButtonKeyDown}
        />
      )}
    </div>
  )
}

export const SearchClearButton = ({ className, ...otherProps }: React.ComponentPropsWithRef<"button">) => {
  return (
    <Button
      variant="unstyle"
      {...otherProps}
      className={cn("hover:bg-surface-hovered absolute top-1/2 right-14 h-9! -translate-y-1/2 p-1.5", className)}>
      <X className="text-on-surface-subdued h-6 w-6" />
    </Button>
  )
}

interface SearchSelectInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  isLoading?: boolean
  label?: string
  error?: boolean
  assistiveText?: string | React.ReactNode
  testId?: string
  onClear?: (event: React.MouseEvent<HTMLButtonElement>) => void
  showClearButton?: boolean
  ref?: React.Ref<HTMLInputElement>
}

export const SearchSelectInput = (props: SearchSelectInputProps) => {
  const {
    className,
    label,
    required,
    error,
    assistiveText,
    testId,
    value,
    onClear,
    showClearButton = true,
    ...inputProps
  } = props
  const id = useId()
  return (
    <div className="relative">
      <input
        id={id}
        data-testid={`${testId}-search-input`}
        autoComplete="off"
        aria-autocomplete="none"
        placeholder=" "
        role="combobox"
        className={cn(
          "peer border-border bg-surface-subdued text-on-surface placeholder:text-on-surface-subdued hover:bg-surface-subdued-hovered focus:outline-primary z-1 h-12 w-full rounded-[8px] border pt-4.75 pr-11.25 pl-3.75 text-sm outline-offset-0 transition-all focus:outline-2",
          className,
          error && "outline-critical! outline-1 focus:outline-2",
          showClearButton && "pr-23.75",
        )}
        value={value}
        {...inputProps}
      />
      <label
        data-testid={`${testId}-search-label`}
        htmlFor={id}
        className={cn(
          "text-on-surface-subdued peer-focus:text-primary absolute top-1 left-4 text-xs transition-all peer-placeholder-shown:top-3.5 peer-placeholder-shown:text-sm peer-focus:top-1 peer-focus:text-xs",
          error && "text-critical peer-focus:text-critical",
        )}>
        {label}
        {required && "*"}
      </label>
      {assistiveText && !error && (
        <p className="text-on-surface-subdued mt-0.75 ml-4 text-xs tracking-normal">{assistiveText}</p>
      )}
      {value && showClearButton && <SearchClearButton onClick={onClear} data-testid={`${testId}-clear-button`} />}
      <span className="pointer-events-none absolute top-1/2 right-1 -translate-y-1/2 p-2">
        <CaretDown className="text-on-surface-subdued h-6 w-6" />
      </span>
    </div>
  )
}

export const SearchList = ({ className, ...props }: React.ComponentPropsWithRef<typeof SearchPrimitive.List>) => (
  <SearchPrimitive.List className={cn("max-h-75 overflow-x-hidden overflow-y-auto", className)} {...props} />
)

export const SearchEmpty = ({ className, ...props }: React.ComponentPropsWithRef<typeof SearchPrimitive.Empty>) => (
  <SearchPrimitive.Empty
    className={cn("text-on-surface relative cursor-default px-4 py-2 select-none", className)}
    {...props}
  />
)

export const SearchContent = ({ className, ...props }: React.ComponentPropsWithRef<typeof SearchPrimitive.Group>) => (
  <SearchPrimitive.Group
    className={cn(
      "bg-surface text-foreground shadow-3 **:[[cmdk-group-heading]]:text-muted-foreground absolute top-12.5 right-0 z-20 mt-0 grid w-full origin-top-right gap-2 overflow-hidden rounded-xl px-2 py-2 has-data-radix-scroll-area-viewport:py-0 **:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:py-1.5 **:[[cmdk-group-heading]]:text-xs **:[[cmdk-group-heading]]:font-medium",
      className,
    )}
    {...props}
  />
)

export const SearchSeparator = ({
  className,
  ...props
}: React.ComponentPropsWithRef<typeof SearchPrimitive.Separator>) => (
  <SearchPrimitive.Separator className={cn("bg-border -mx-1 h-px", className)} {...props} />
)

export const SearchItem = ({ className, ...props }: React.ComponentPropsWithRef<typeof SearchPrimitive.Item>) => (
  <SearchPrimitive.Item
    className={cn(
      "aria-selected:bg-surface-hovered aria-selected:text-on-surface hover:bg-surface-hovered relative cursor-pointer items-center rounded-xl px-2 py-1.5 text-sm select-none data-disabled:pointer-events-none data-disabled:opacity-50",
      className,
    )}
    {...props}
  />
)
